import { Box, Chip, useTheme } from '@mui/material'
import { useState } from 'react'

interface Route {
    shortName: string
    color: string
    textColor: string
}

interface RouteFilterProps {
    allRoutes: Route[]
    selectedRoutes: Set<string>
    onRouteSelect: (routeShortName: string, isFirstChange?: boolean) => void
    onResetFilter: () => void
    stopId: string
}

export const RouteFilter = ({
    allRoutes,
    selectedRoutes,
    onRouteSelect,
    onResetFilter,
    stopId,
}: RouteFilterProps) => {
    const theme = useTheme()
    const [firstChange, setFirstChange] = useState(true)

    // Hide filter for IJPP stops
    if (allRoutes.length === 0 || stopId.includes('IJPP')) {
        return null
    }

    return (
        <Box sx={{ marginTop: 1 }}>
            <Box
                sx={{
                    'display': 'flex',
                    'overflowX': 'auto',
                    'gap': 1,
                    '&::-webkit-scrollbar': {
                        height: '4px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: theme.palette.divider,
                        borderRadius: '2px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        background: theme.palette.action.disabled,
                    },
                }}
            >
                {allRoutes.map(route => (
                    <Chip
                        key={route.shortName}
                        label={route.shortName}
                        variant={selectedRoutes.has(route.shortName) ? 'filled' : 'outlined'}
                        onClick={() => {
                            if (firstChange) {
                                // First click: isolate this route
                                onRouteSelect(route.shortName, true)
                                setFirstChange(false)
                            }
                            else {
                                // Check if this is the last selected route
                                if (selectedRoutes.size === 1 && selectedRoutes.has(route.shortName)) {
                                    // Deselecting the last route - select all and reset
                                    onResetFilter()
                                    setFirstChange(true)
                                }
                                else {
                                    // Toggle route
                                    onRouteSelect(route.shortName, false)
                                }
                            }
                        }}
                        sx={{
                            'flexShrink': 0,
                            'height': '32px',
                            'borderRadius': '999px',
                            'fontSize': '1rem',
                            'fontWeight': 600,
                            'backgroundColor': selectedRoutes.has(route.shortName) ? route.color : 'transparent',
                            'color': selectedRoutes.has(route.shortName) ? route.textColor : 'inherit',
                            'border': `1.5px solid ${route.color}`,
                            'cursor': 'pointer',
                            'transition': 'all 0.2s ease',
                            '& .MuiChip-label': {
                                paddingX: '.6rem',
                            },
                            '&:hover': {
                                backgroundColor: selectedRoutes.has(route.shortName) ? route.color : `${route.color}22`,
                            },
                        }}
                    />
                ))}
            </Box>
        </Box>
    )
}
